/**
 * SchoolTransport — Bus Routes, Stops, Student Assignments
 * Enhanced with edit, proper tabs, stop selection, delete confirmations
 */
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Bus, Plus, Loader2, MapPin, Users, Trash2, Edit2, UserPlus, Route, ArrowLeft } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, { ...opts, headers: { ...authHeaders(), 'Content-Type': 'application/json', ...opts.headers } });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Request failed'); }
  return res.json();
};

const StatCard = ({ label, value, icon: Icon, color }) => (
  <Card><CardContent className="p-3 text-center"><Icon className={`h-5 w-5 mx-auto mb-1 text-${color}-500`} /><p className="text-xl font-bold text-slate-900">{value}</p><p className="text-xs text-slate-500">{label}</p></CardContent></Card>
);

// ==================== ROUTE FORM DIALOG ====================
const RouteFormDialog = ({ open, onClose, tenantId, editing }) => {
  const qc = useQueryClient();
  const defaults = { name: '', route_number: '', driver_name: '', driver_phone: '', vehicle_number: '', capacity: 40, description: '', is_active: true };
  const [form, setForm] = useState(defaults);

  useEffect(() => {
    if (open) setForm(editing ? { name: editing.name || '', route_number: editing.route_number || '', driver_name: editing.driver_name || '', driver_phone: editing.driver_phone || '', vehicle_number: editing.vehicle_number || '', capacity: editing.capacity || 40, description: editing.description || '', is_active: editing.is_active !== false } : defaults);
  }, [open, editing]);

  const mutation = useMutation({
    mutationFn: d => editing
      ? api(`/tenants/${tenantId}/transport/routes/${editing.id}`, { method: 'PUT', body: JSON.stringify(d) })
      : api(`/tenants/${tenantId}/transport/routes`, { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success(editing ? 'Route updated' : 'Route created'); qc.invalidateQueries({ queryKey: ['transport-routes'] }); qc.invalidateQueries({ queryKey: ['transport-dashboard'] }); onClose(); },
    onError: e => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} Bus Route</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Route Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="route-name" /></div>
            <div><Label>Route Number</Label><Input value={form.route_number} onChange={e => setForm({ ...form, route_number: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Driver Name</Label><Input value={form.driver_name} onChange={e => setForm({ ...form, driver_name: e.target.value })} /></div>
            <div><Label>Driver Phone</Label><Input value={form.driver_phone} onChange={e => setForm({ ...form, driver_phone: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Vehicle Number</Label><Input value={form.vehicle_number} onChange={e => setForm({ ...form, vehicle_number: e.target.value })} /></div>
            <div><Label>Capacity</Label><Input type="number" value={form.capacity} onChange={e => setForm({ ...form, capacity: parseInt(e.target.value) || 40 })} /></div>
          </div>
          <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} /><Label>Active</Label></div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          <Button onClick={() => mutation.mutate(form)} disabled={!form.name || mutation.isPending} data-testid="save-route-btn">
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{editing ? 'Update' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== STOP FORM DIALOG ====================
const StopFormDialog = ({ open, onClose, tenantId, routeId, editing }) => {
  const qc = useQueryClient();
  const defaults = { name: '', route_id: routeId || '', order: 0, pickup_time: '', dropoff_time: '', address: '' };
  const [form, setForm] = useState(defaults);

  useEffect(() => {
    if (open) setForm(editing ? { name: editing.name || '', route_id: editing.route_id || routeId || '', order: editing.order || 0, pickup_time: editing.pickup_time || '', dropoff_time: editing.dropoff_time || '', address: editing.address || '' } : { ...defaults, route_id: routeId || '' });
  }, [open, editing, routeId]);

  const mutation = useMutation({
    mutationFn: d => editing
      ? api(`/tenants/${tenantId}/transport/stops/${editing.id}`, { method: 'PUT', body: JSON.stringify(d) })
      : api(`/tenants/${tenantId}/transport/stops`, { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success(editing ? 'Stop updated' : 'Stop added'); qc.invalidateQueries({ queryKey: ['transport-stops'] }); qc.invalidateQueries({ queryKey: ['transport-dashboard'] }); onClose(); },
    onError: e => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} Bus Stop</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Stop Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="stop-name" /></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><Label>Order</Label><Input type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 0 })} /></div>
            <div><Label>Pickup</Label><Input type="time" value={form.pickup_time} onChange={e => setForm({ ...form, pickup_time: e.target.value })} /></div>
            <div><Label>Drop-off</Label><Input type="time" value={form.dropoff_time} onChange={e => setForm({ ...form, dropoff_time: e.target.value })} /></div>
          </div>
          <div><Label>Address</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          <Button onClick={() => mutation.mutate(form)} disabled={!form.name || mutation.isPending} data-testid="save-stop-btn">
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{editing ? 'Update' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== MAIN COMPONENT ====================
export const SchoolTransport = ({ tenantId }) => {
  const qc = useQueryClient();
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [showRouteForm, setShowRouteForm] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [showStopForm, setShowStopForm] = useState(false);
  const [editingStop, setEditingStop] = useState(null);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [assignForm, setAssignForm] = useState({ student_id: '', route_id: '', stop_id: '' });

  const { data: dashboard } = useQuery({ queryKey: ['transport-dashboard', tenantId], queryFn: () => api(`/tenants/${tenantId}/transport/dashboard`) });
  const { data: routes = [], isLoading } = useQuery({ queryKey: ['transport-routes', tenantId], queryFn: () => api(`/tenants/${tenantId}/transport/routes`) });
  const { data: stops = [] } = useQuery({ queryKey: ['transport-stops', tenantId, selectedRoute], queryFn: () => api(`/tenants/${tenantId}/transport/routes/${selectedRoute}/stops`), enabled: !!selectedRoute });
  const { data: routeStudents = [] } = useQuery({ queryKey: ['transport-students', tenantId, selectedRoute], queryFn: () => api(`/tenants/${tenantId}/transport/routes/${selectedRoute}/students`), enabled: !!selectedRoute });
  const { data: students = [] } = useQuery({ queryKey: ['transport-all-students', tenantId], queryFn: () => api(`/tenants/${tenantId}/students`).then(d => d.students || d).catch(() => []) });

  const deleteRoute = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/transport/routes/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Route deleted'); setSelectedRoute(null); qc.invalidateQueries({ queryKey: ['transport-routes'] }); qc.invalidateQueries({ queryKey: ['transport-dashboard'] }); },
    onError: e => toast.error(e.message),
  });

  const deleteStop = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/transport/stops/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Stop deleted'); qc.invalidateQueries({ queryKey: ['transport-stops'] }); qc.invalidateQueries({ queryKey: ['transport-dashboard'] }); },
    onError: e => toast.error(e.message),
  });

  const assignStudent = useMutation({
    mutationFn: d => api(`/tenants/${tenantId}/transport/assign`, { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success('Student assigned'); setShowAssignForm(false); setAssignForm({ student_id: '', route_id: '', stop_id: '' }); qc.invalidateQueries({ queryKey: ['transport-students'] }); qc.invalidateQueries({ queryKey: ['transport-routes'] }); qc.invalidateQueries({ queryKey: ['transport-dashboard'] }); },
    onError: e => toast.error(e.message),
  });

  const unassign = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/transport/assign/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Student removed'); qc.invalidateQueries({ queryKey: ['transport-students'] }); qc.invalidateQueries({ queryKey: ['transport-routes'] }); qc.invalidateQueries({ queryKey: ['transport-dashboard'] }); },
    onError: e => toast.error(e.message),
  });

  // Route detail with stops + students
  const routeDetail = selectedRoute ? routes.find(r => r.id === selectedRoute) : null;

  return (
    <div className="space-y-5" data-testid="school-transport">
      <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2"><Bus className="h-5 w-5" /> Transport</h2>

      {/* Dashboard */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="transport-dashboard">
          <StatCard label="Routes" value={dashboard.total_routes} icon={Bus} color="indigo" />
          <StatCard label="Stops" value={dashboard.total_stops} icon={MapPin} color="emerald" />
          <StatCard label="Assigned" value={dashboard.students_assigned} icon={Users} color="blue" />
          <StatCard label="Unassigned" value={dashboard.unassigned} icon={Users} color="amber" />
          <StatCard label="Capacity" value={dashboard.total_capacity} icon={Bus} color="slate" />
          <StatCard label="Utilization" value={`${dashboard.utilization}%`} icon={Route} color="rose" />
        </div>
      )}

      <Tabs defaultValue="routes">
        <TabsList>
          <TabsTrigger value="routes" data-testid="transport-tab-routes"><Bus className="h-4 w-4 mr-1" /> Routes</TabsTrigger>
          <TabsTrigger value="assignments" data-testid="transport-tab-assignments"><Users className="h-4 w-4 mr-1" /> Assignments</TabsTrigger>
          <TabsTrigger value="gate-passes" data-testid="transport-tab-gate-passes"><Route className="h-4 w-4 mr-1" /> Gate Passes</TabsTrigger>
        </TabsList>

        {/* ROUTES TAB */}
        <TabsContent value="routes" className="mt-4 space-y-4">
          {routeDetail ? (
            // Route detail view
            <div className="space-y-4" data-testid="route-detail">
              <div className="flex items-center justify-between">
                <button onClick={() => setSelectedRoute(null)} className="text-xs text-indigo-600 hover:underline flex items-center gap-1"><ArrowLeft className="h-3 w-3" /> All Routes</button>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setEditingRoute(routeDetail); setShowRouteForm(true); }}><Edit2 className="h-3.5 w-3.5 mr-1" />Edit</Button>
                  <Button size="sm" variant="outline" className="text-red-500" onClick={() => { if (window.confirm('Delete this route and all stops/assignments?')) deleteRoute.mutate(routeDetail.id); }}>
                    <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
                  </Button>
                </div>
              </div>
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle>{routeDetail.name}</CardTitle>
                      <div className="flex gap-3 text-sm text-slate-500 mt-1">
                        {routeDetail.route_number && <span>Route #{routeDetail.route_number}</span>}
                        <span>{routeDetail.vehicle_number || 'No vehicle'}</span>
                        <span>{routeDetail.student_count}/{routeDetail.capacity} students</span>
                      </div>
                    </div>
                    <Badge className={routeDetail.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>{routeDetail.is_active ? 'Active' : 'Inactive'}</Badge>
                  </div>
                  {routeDetail.driver_name && <p className="text-sm text-slate-600 mt-1">Driver: {routeDetail.driver_name} {routeDetail.driver_phone && `(${routeDetail.driver_phone})`}</p>}
                </CardHeader>
              </Card>

              {/* Stops */}
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">Stops ({stops.length})</h3>
                <Button size="sm" variant="outline" onClick={() => { setEditingStop(null); setShowStopForm(true); }} data-testid="add-stop-btn"><Plus className="h-4 w-4 mr-1" /> Add Stop</Button>
              </div>
              <Card>
                {stops.length === 0 ? <CardContent className="py-8 text-center text-slate-400">No stops added</CardContent> : (
                  <Table>
                    <TableHeader><TableRow><TableHead className="w-[50px]">#</TableHead><TableHead>Stop</TableHead><TableHead>Pickup</TableHead><TableHead>Drop-off</TableHead><TableHead>Address</TableHead><TableHead>Students</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {stops.map(s => (
                        <TableRow key={s.id} data-testid={`stop-row-${s.id}`}>
                          <TableCell className="text-slate-400">{s.order}</TableCell>
                          <TableCell className="font-medium">{s.name}</TableCell>
                          <TableCell className="text-xs">{s.pickup_time || '-'}</TableCell>
                          <TableCell className="text-xs">{s.dropoff_time || '-'}</TableCell>
                          <TableCell className="text-xs text-slate-500">{s.address || '-'}</TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{s.student_count}</Badge></TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingStop(s); setShowStopForm(true); }}><Edit2 className="h-3.5 w-3.5" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { if (window.confirm(`Delete stop "${s.name}"?`)) deleteStop.mutate(s.id); }}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>

              {/* Route Students */}
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">Students ({routeStudents.length})</h3>
                <Button size="sm" variant="outline" onClick={() => { setAssignForm({ student_id: '', route_id: selectedRoute, stop_id: '' }); setShowAssignForm(true); }} data-testid="assign-student-btn"><UserPlus className="h-4 w-4 mr-1" /> Assign Student</Button>
              </div>
              <Card>
                {routeStudents.length === 0 ? <CardContent className="py-8 text-center text-slate-400">No students assigned</CardContent> : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Student</TableHead><TableHead>Adm No.</TableHead><TableHead>Stop</TableHead><TableHead></TableHead></TableRow></TableHeader>
                    <TableBody>
                      {routeStudents.map(s => (
                        <TableRow key={s.id} data-testid={`student-row-${s.id}`}>
                          <TableCell className="font-medium text-sm">{s.student_name}</TableCell>
                          <TableCell className="text-xs text-slate-500">{s.admission_number}</TableCell>
                          <TableCell className="text-sm">{s.stop_name || <span className="text-slate-400">Not set</span>}</TableCell>
                          <TableCell><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { if (window.confirm(`Remove ${s.student_name}?`)) unassign.mutate(s.id); }}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>
            </div>
          ) : (
            // Routes list
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">{routes.length} routes</p>
                <Button size="sm" onClick={() => { setEditingRoute(null); setShowRouteForm(true); }} data-testid="add-route-btn"><Plus className="h-4 w-4 mr-1" /> Add Route</Button>
              </div>
              {isLoading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : routes.length === 0 ? (
                <Card className="border-dashed"><CardContent className="py-10 text-center text-slate-400"><Bus className="h-8 w-8 mx-auto mb-2 opacity-50" />No routes yet</CardContent></Card>
              ) : (
                <div className="grid md:grid-cols-2 gap-3">
                  {routes.map(r => (
                    <Card key={r.id} className="cursor-pointer hover:border-indigo-200 transition-colors" onClick={() => setSelectedRoute(r.id)} data-testid={`route-card-${r.id}`}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-semibold text-slate-900">{r.name}</h4>
                            {r.route_number && <p className="text-xs text-slate-500">Route #{r.route_number}</p>}
                            <div className="flex gap-3 text-xs text-slate-500 mt-1">
                              <span><Bus className="h-3 w-3 inline mr-0.5" />{r.vehicle_number || 'N/A'}</span>
                              <span><Users className="h-3 w-3 inline mr-0.5" />{r.student_count}/{r.capacity}</span>
                              <span><MapPin className="h-3 w-3 inline mr-0.5" />{r.stop_count} stops</span>
                            </div>
                            {r.driver_name && <p className="text-xs text-slate-500 mt-1">Driver: {r.driver_name}</p>}
                          </div>
                          <Badge className={r.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>{r.is_active ? 'Active' : 'Inactive'}</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* ASSIGNMENTS TAB */}
        <TabsContent value="assignments" className="mt-4 space-y-3">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">Select a route to view/manage student assignments</p>
            <Button size="sm" onClick={() => setShowAssignForm(true)} data-testid="assign-student-btn2"><UserPlus className="h-4 w-4 mr-1" /> Assign Student</Button>
          </div>
          <Select value={selectedRoute || ''} onValueChange={setSelectedRoute}>
            <SelectTrigger className="w-full max-w-[280px]" data-testid="assignment-route-select"><SelectValue placeholder="Select route" /></SelectTrigger>
            <SelectContent>{routes.map(r => <SelectItem key={r.id} value={r.id}>{r.name} ({r.student_count}/{r.capacity})</SelectItem>)}</SelectContent>
          </Select>
          {selectedRoute ? (
            <Card>
              {routeStudents.length === 0 ? <CardContent className="py-8 text-center text-slate-400">No students assigned to this route</CardContent> : (
                <Table>
                  <TableHeader><TableRow><TableHead>Student</TableHead><TableHead>Adm No.</TableHead><TableHead>Stop</TableHead><TableHead></TableHead></TableRow></TableHeader>
                  <TableBody>
                    {routeStudents.map(s => (
                      <TableRow key={s.id}>
                        <TableCell className="font-medium text-sm">{s.student_name}</TableCell>
                        <TableCell className="text-xs text-slate-500">{s.admission_number}</TableCell>
                        <TableCell className="text-sm">{s.stop_name || '-'}</TableCell>
                        <TableCell><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { if (window.confirm(`Remove ${s.student_name}?`)) unassign.mutate(s.id); }}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          ) : null}
        </TabsContent>

        {/* GATE PASSES TAB */}
        <TabsContent value="gate-passes" className="mt-4">
          <VehicleGatePassPanel tenantId={tenantId} />
        </TabsContent>
      </Tabs>

      {/* Route Form Dialog */}
      <RouteFormDialog open={showRouteForm} onClose={() => { setShowRouteForm(false); setEditingRoute(null); }} tenantId={tenantId} editing={editingRoute} />

      {/* Stop Form Dialog */}
      <StopFormDialog open={showStopForm} onClose={() => { setShowStopForm(false); setEditingStop(null); }} tenantId={tenantId} routeId={selectedRoute} editing={editingStop} />

      {/* Assign Student Dialog */}
      <Dialog open={showAssignForm} onOpenChange={setShowAssignForm}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign Student to Route</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Route *</Label>
              <Select value={assignForm.route_id} onValueChange={v => setAssignForm({ ...assignForm, route_id: v, stop_id: '' })}>
                <SelectTrigger data-testid="assign-route-select"><SelectValue placeholder="Select route" /></SelectTrigger>
                <SelectContent>{routes.map(r => <SelectItem key={r.id} value={r.id}>{r.name} ({r.student_count}/{r.capacity})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Student *</Label>
              <Select value={assignForm.student_id} onValueChange={v => setAssignForm({ ...assignForm, student_id: v })}>
                <SelectTrigger data-testid="assign-student-select"><SelectValue placeholder="Select student" /></SelectTrigger>
                <SelectContent>{(Array.isArray(students) ? students : []).map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.admission_number})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {assignForm.route_id && stops.length > 0 && (
              <div><Label>Stop (optional)</Label>
                <Select value={assignForm.stop_id} onValueChange={v => setAssignForm({ ...assignForm, stop_id: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Select stop" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">No specific stop</SelectItem>{stops.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => assignStudent.mutate(assignForm)} disabled={!assignForm.route_id || !assignForm.student_id || assignStudent.isPending} data-testid="save-assign-btn">
              {assignStudent.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SchoolTransport;

// ============== VEHICLE GATE PASS PANEL ==============
const gpApi = {
  vehicles: (t, h) => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/school/tenants/${t}/transport/vehicles`, { headers: h }).then(r => r.json()),
  open: (t, h) => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/school/tenants/${t}/transport/gate-passes/open`, { headers: h }).then(r => r.json()),
  recent: (t, h) => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/school/tenants/${t}/transport/gate-passes?limit=30`, { headers: h }).then(r => r.json()),
  issue: (t, h, d) => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/school/tenants/${t}/transport/gate-passes`, { method: 'POST', headers: { ...h, 'Content-Type': 'application/json' }, body: JSON.stringify(d) }).then(r => r.json()),
  close: (t, h, id, d) => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/school/tenants/${t}/transport/gate-passes/${id}/return`, { method: 'POST', headers: { ...h, 'Content-Type': 'application/json' }, body: JSON.stringify(d) }).then(r => r.json()),
};

const VehicleGatePassPanel = ({ tenantId }) => {
  const qc = useQueryClient();
  const headers = authHeaders();
  const { data: vehicles = [] } = useQuery({ queryKey: ['gp-vehicles', tenantId], queryFn: () => gpApi.vehicles(tenantId, headers) });
  const { data: openPasses = [] } = useQuery({ queryKey: ['gp-open', tenantId], queryFn: () => gpApi.open(tenantId, headers), refetchInterval: 30000 });
  const { data: recent = [] } = useQuery({ queryKey: ['gp-recent', tenantId], queryFn: () => gpApi.recent(tenantId, headers) });
  const [form, setForm] = useState({ vehicle_id: '', driver_name: '', driver_phone: '', destination: '', purpose: '', fuel_level_out: 100, mileage_out: 0, expected_return_at: '' });
  const [closeForm, setCloseForm] = useState({ passId: null, fuel_level_in: 100, mileage_in: 0, notes: '' });
  const refresh = () => ['gp-open', 'gp-recent'].forEach(k => qc.invalidateQueries({ queryKey: [k, tenantId] }));

  const issue = async () => {
    if (!form.vehicle_id || !form.driver_name || !form.destination) { toast.error('Vehicle, driver and destination are required'); return; }
    const payload = { ...form, fuel_level_out: Number(form.fuel_level_out), mileage_out: Number(form.mileage_out) };
    const r = await gpApi.issue(tenantId, headers, payload);
    if (r.pass_number) { toast.success(`Pass issued: ${r.pass_number}`); setForm({ vehicle_id: '', driver_name: '', driver_phone: '', destination: '', purpose: '', fuel_level_out: 100, mileage_out: 0, expected_return_at: '' }); refresh(); }
    else toast.error(r.detail || 'Failed');
  };
  const doClose = async () => {
    const r = await gpApi.close(tenantId, headers, closeForm.passId, { fuel_level_in: Number(closeForm.fuel_level_in), mileage_in: Number(closeForm.mileage_in), notes: closeForm.notes });
    if (r.success) { toast.success(`Returned · ${Number(r.distance_covered || 0).toFixed(1)} km`); setCloseForm({ passId: null, fuel_level_in: 100, mileage_in: 0, notes: '' }); refresh(); }
    else toast.error(r.detail || 'Failed');
  };

  return (
    <div className="space-y-3" data-testid="gate-pass-panel">
      {/* Issue form */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Plus className="h-4 w-4" /> Issue Gate Pass</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-6 gap-2">
          <Select value={form.vehicle_id} onValueChange={v => setForm({ ...form, vehicle_id: v })}>
            <SelectTrigger data-testid="gp-vehicle-select"><SelectValue placeholder="Vehicle" /></SelectTrigger>
            <SelectContent>{vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.registration_number} ({v.make} {v.model})</SelectItem>)}</SelectContent>
          </Select>
          <Input placeholder="Driver name" value={form.driver_name} onChange={e => setForm({ ...form, driver_name: e.target.value })} data-testid="gp-driver-name" />
          <Input placeholder="Driver phone" value={form.driver_phone} onChange={e => setForm({ ...form, driver_phone: e.target.value })} />
          <Input className="md:col-span-2" placeholder="Destination" value={form.destination} onChange={e => setForm({ ...form, destination: e.target.value })} data-testid="gp-destination" />
          <Input placeholder="Purpose" value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })} />
          <Input type="number" placeholder="Fuel % out" value={form.fuel_level_out} onChange={e => setForm({ ...form, fuel_level_out: e.target.value })} data-testid="gp-fuel-out" />
          <Input type="number" placeholder="Mileage (km)" value={form.mileage_out} onChange={e => setForm({ ...form, mileage_out: e.target.value })} data-testid="gp-mileage-out" />
          <Input type="datetime-local" placeholder="Expected return" value={form.expected_return_at} onChange={e => setForm({ ...form, expected_return_at: e.target.value })} />
          <div className="md:col-span-3" />
          <Button onClick={issue} data-testid="gp-issue-btn">Issue Pass</Button>
        </CardContent>
      </Card>

      {/* Open passes (vehicles currently out) */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Out on the road ({openPasses.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {openPasses.length === 0 && <p className="text-sm text-slate-500 py-2">All vehicles are at the gate.</p>}
          {openPasses.map(p => (
            <div key={p.id} className={`flex flex-wrap items-center gap-2 p-2 rounded-md border ${p.overdue ? 'border-rose-300 bg-rose-50' : 'border-slate-200 bg-slate-50'}`} data-testid={`gp-open-${p.id}`}>
              <span className="text-xs font-mono text-slate-500">{p.pass_number}</span>
              <span className="text-sm font-semibold">{p.vehicle_registration}</span>
              <span className="text-sm">·  {p.driver_name}</span>
              <span className="text-sm">→ {p.destination}</span>
              <span className="text-[11px] text-slate-500">Out: {p.fuel_level_out}% · {p.mileage_out} km</span>
              {p.overdue && <Badge variant="destructive" className="text-[10px]">OVERDUE</Badge>}
              <Button size="sm" variant="outline" className="ml-auto" onClick={() => setCloseForm({ passId: p.id, fuel_level_in: p.fuel_level_out || 100, mileage_in: p.mileage_out, notes: '' })} data-testid={`gp-close-${p.id}`}>Return</Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Recent passes */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Recent History</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>Pass</TableHead><TableHead>Vehicle</TableHead><TableHead>Driver</TableHead><TableHead>Destination</TableHead><TableHead>Distance</TableHead><TableHead>Fuel</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {recent.map(p => (
                <TableRow key={p.id} data-testid={`gp-row-${p.id}`}>
                  <TableCell className="text-xs font-mono">{p.pass_number}</TableCell>
                  <TableCell className="text-sm">{p.vehicle_registration}</TableCell>
                  <TableCell className="text-sm">{p.driver_name}</TableCell>
                  <TableCell className="text-sm truncate max-w-xs">{p.destination}</TableCell>
                  <TableCell className="text-sm">{p.distance_covered != null ? `${Number(p.distance_covered).toFixed(1)} km` : '—'}</TableCell>
                  <TableCell className="text-sm">{p.fuel_level_out != null && p.fuel_level_in != null ? `${p.fuel_level_out}%→${p.fuel_level_in}%` : '—'}</TableCell>
                  <TableCell><Badge variant="outline" className={`text-[10px] ${p.status === 'out' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>{p.status}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Close pass dialog */}
      <Dialog open={!!closeForm.passId} onOpenChange={(o) => !o && setCloseForm({ passId: null, fuel_level_in: 100, mileage_in: 0, notes: '' })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Close Gate Pass · Vehicle Return</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Fuel % on return</Label><Input type="number" value={closeForm.fuel_level_in} onChange={e => setCloseForm({ ...closeForm, fuel_level_in: e.target.value })} data-testid="gp-fuel-in" /></div>
              <div><Label className="text-xs">Mileage (km)</Label><Input type="number" value={closeForm.mileage_in} onChange={e => setCloseForm({ ...closeForm, mileage_in: e.target.value })} data-testid="gp-mileage-in" /></div>
            </div>
            <div><Label className="text-xs">Notes</Label><Input value={closeForm.notes} onChange={e => setCloseForm({ ...closeForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={doClose} data-testid="gp-confirm-return-btn">Confirm Return</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

